import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  TextField,
  InputAdornment,
  Button,
  ButtonGroup,
  Stack,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import Navbar from "../../components/Navbar";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/Footer";
import villas from "../../constants/villas.json";

const Properties = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("north goa");
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out",
      once: true,
      offset: 100,
    });
  }, []);
  // Use imported villas data
  const properties = villas;

  // Filter properties based on search and location
  const filteredProperties = properties.filter((property) => {
    const matchesSearch =
      property.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      property.description.toLowerCase().includes(searchQuery.toLowerCase());
    // Normalize both selectedLocation and property.location to lowercase for comparison
    const normalizedLocation = (property.location || "").toLowerCase();
    const matchesLocation = normalizedLocation === selectedLocation;
    return matchesSearch && matchesLocation;
  });

  const handleLocationChange = (location) => {
    setSelectedLocation(location.toLowerCase());
  };

  const handlePropertyClick = (propertyId) => {
    navigate(`/property/${propertyId}`);
  };

  return (
    <>
      <Navbar />
      <Box sx={{ py: 8 }}>
        <Container maxWidth="xl">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
          >
            {/* Header Section */}
            <Typography
              variant="h2"
              data-aos="fade-up"
              sx={{
                fontFamily: "Monument-Private",
                fontWeight: 700,
                fontSize: { xs: "2.5rem", md: "4rem" },
                mb: 4,
                color: "#000",
              }}
            >
              Stays in Goa
            </Typography>

            {/* Search Bar */}
            <Box sx={{ maxWidth: "600px", mb: 4 }}>
              <TextField
                fullWidth
                placeholder="Search stays"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-aos="fade-up"
                data-aos-delay="50"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#666", fontSize: "1.2rem" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "50px",
                    backgroundColor: "#e8e9ea",
                    pr: 12,
                    border: "none",
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                    "& input": {
                      py: 2,
                      fontSize: "1rem",
                    },
                  },
                }}
              />
            </Box>
          </Stack>

          {/* Location Toggle */}
          <Box
            data-aos="fade-up"
            data-aos-delay="100"
            sx={{
              mb: 4,
              display: "flex",
              background: "#f2f3f5",
              borderRadius: 100,
              width: "fit-content",
            }}
          >
            <ButtonGroup
              sx={{
                "& .MuiButton-root": {
                  width: "fit-content",
                  alignItems: "flex-start",
                  px: 4,
                  py: 1.5,
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "1rem",
                  border: "none",
                  borderRadius: 100,
                },
              }}
            >
              <Button
                onClick={() => handleLocationChange("North Goa")}
                sx={{
                  backgroundColor:
                    selectedLocation === "north goa" ? "#000" : "transparent",
                  color: selectedLocation === "north goa" ? "#fff" : "#666",
                }}
              >
                North goa
              </Button>
              <Button
                onClick={() => handleLocationChange("South Goa")}
                sx={{
                  borderRadius: 100,
                  backgroundColor:
                    selectedLocation === "south goa" ? "#000" : "transparent",
                  color: selectedLocation === "south goa" ? "#fff" : "#666",
                }}
              >
                South goa
              </Button>
            </ButtonGroup>
          </Box>

          {/* Properties Grid */}
          <Grid container spacing={4}>
            {filteredProperties.map((property, index) => (
              <Grid item size={{ xs: 12, md: 8, lg: 4 }} key={property.id} sx={{ display: 'flex' }}>
                <Card
                  onClick={() => handlePropertyClick(property.id)}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                  sx={{
                    borderRadius: 4,
                    overflow: "hidden",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    position: 'relative',
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 8px 40px rgba(0, 0, 0, 0.12)",
                    },
                  }}
                >
                  {/* Rating in top right corner */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 20,
                      right: 20,
                      zIndex: 2,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      backgroundColor: 'rgba(255,255,255, 0.8)',
                      p: 1.5,
                      borderRadius: 2,
                      backdropFilter: 'blur(10px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    }}
                  >
                    <Box
                      sx={{
                        width: "1.2rem",
                        height: "1.2rem",
                        backgroundImage: "url(/star.svg)",
                        backgroundSize: "contain",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "Monument-Private",
                        fontWeight: 700,
                        fontSize: "1.1rem",
                      }}
                    >
                      {property.reviews && property.reviews.length > 0
                        ? (
                            property.reviews.reduce((sum, r) => sum + (r.rating || 0), 0) /
                            property.reviews.length
                          ).toFixed(1)
                        : '—'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.9rem",
                      }}
                    >
                      /5
                    </Typography>
                  </Box>
                  <CardMedia
                    component="img"
                    height="280"
                    image={property.image}
                    alt={property.name}
                    sx={{
                      objectFit: "cover",
                    }}
                  />
                  <CardContent sx={{ p: 3, flexGrow: 1 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: "Monument-Private",
                        fontWeight: 600,
                        fontSize: "1.5rem",
                        color: "#000",
                        mb: 1,
                      }}
                    >
                      {property.name}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#666",
                        lineHeight: 1.6,
                        fontSize: "0.95rem",
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minHeight: '3.2em',
                      }}
                    >
                      {property.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* No Results Message */}
          {filteredProperties.length === 0 && (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography
                variant="h5"
                sx={{
                  color: "#666",
                  fontWeight: 500,
                  mb: 2,
                }}
              >
                No properties found
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "#999",
                }}
              >
                Try adjusting your search or location filter
              </Typography>
            </Box>
          )}
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default Properties;
